"use client";

import { clsx } from "clsx";
import type { ReactNode } from "react";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { PhotoSplit } from "./PhotoSplit";
import { QuizStepImageCarousel } from "./QuizStepImageCarousel";
import type { StepImageEntry } from "./QuizStepImage";
import { NavFooter } from "./NavFooter";

type NavProps = {
  onNext: () => void;
  onBack?: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
  secondary?: { label: string; onClick: () => void; disabled?: boolean; onBlocked?: () => void };
  onNextBlocked?: () => void;
};

type Props = {
  step: string;
  imageOrder: StepImageEntry[];
  /** Eager-load this step's hero image: true only for whichever step
   * actually renders first — normally "priorities", but init() can resume
   * a returning visitor straight onto "constraints" (see QuizWizard), which
   * makes that step's image the real LCP instead. */
  imagePriority?: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  nav: NavProps;
  children: ReactNode;
  /** Error/alert content rendered inside the form column, above the first
   * field — keeps it within the column's own slack instead of stacking
   * above the `PhotoSplit` grid, where it would force the page to overflow. */
  banner?: ReactNode;
};

export function QuizStepShell({
  step,
  imageOrder,
  imagePriority,
  title,
  subtitle,
  nav,
  children,
  banner,
}: Props) {
  return (
    <PhotoSplit
      media={<QuizStepImageCarousel images={imageOrder} priority={imagePriority} />}
      header={
        <>
          {/* size="base": matches QuizStepReview.tsx and HowItWorks.tsx's
              existing size="base" for the same "step label" role — the
              default xs (12px) read too small next to the H1. */}
          <Eyebrow size="base">{step}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-display-sm font-bold tracking-tight text-balance text-text">
            {title}
          </h1>
          {/* Always rendered at md+, even with no subtitle text: reserves
              space (md:min-h-12 == text-base's own 1.5-line-height doubled,
              48px == 2 lines) so the header block is the same total height
              on every step. Without this, constraints (no subtitle) has a
              shorter header than priorities (subtitle present), so the
              photo box below it — same column, in flow — starts at a
              different y, reading as an image "jump" when navigating
              between steps.

              2 lines, not 1: priorities' subtitle is ~88 characters and
              md:max-w-none (below) lets it wrap against the photo column's
              real width — at the narrow end of md (~768px, ~336px of text
              width) that's plausibly 3 lines, not the 1 a smaller reserve
              assumed, reopening the jump in that range.
              2 lines is a deliberate partial fix, not a full guarantee: it
              closes the common case (e.g. ~1024px tablet-landscape) cheaply,
              at the cost of some dead space on constraints at wider widths
              where priorities' subtitle does fit on one line. Closing the
              gap all the way down to 768px would need either trimming the
              subtitle copy (a content decision, not this component's to
              make) or measuring each step's real header height dynamically
              — real added complexity for a viewport range nobody has
              actually tested this layout against.

              Mobile only gets the margin/width treatment when a subtitle is
              actually provided, so a subtitle-less step gets no ghost gap
              there. md:max-w-none: the 480px prose cap was sized for when
              this header lived in the narrower form column; it now sits in
              the wider photo column, so let real subtitle text wrap against
              that instead, same as the H1. */}
          <p
            className={clsx(
              "text-base text-text-muted md:mt-3 md:min-h-12",
              subtitle && "mt-3 max-w-quiz-prose md:max-w-none",
            )}
          >
            {subtitle}
          </p>
        </>
      }
    >
      {/* min-h-full + md:flex-1: fills the grid column, which PhotoSplit now
          makes viewport-height at md+ (md:min-h-[calc(100vh-var(--bsa-nav-h))]),
          so NavFooter's md:mt-auto below has a consistent amount of free
          space to sit at the bottom of on every step regardless of content
          height — the actual fix for the footer landing at a different y
          on priorities vs constraints. This intentionally reverses the
          design handoff's "drop flex-1 so the buttons sit directly below
          the last question" (README §5): cross-step consistency wins.

          md:pt-24: DELIBERATELY much larger than the photo column's own top
          offset (md:pt-0/-mt-2 — see PhotoSplit.tsx). Nic wants the first
          field to line up with the subheading's line — the title side has
          very little further room to move (see PhotoSplit.tsx's comment),
          so this offset is the lever actually doing most of that work.
          Don't re-sync this back to a shared offset with the photo column. */}
      <div className="flex flex-col min-h-full md:flex-1 px-6 pb-6 pt-0 sm:px-8 sm:pb-8 lg:px-10 lg:pb-10 md:pt-24">
        <div className="flex-1 md:flex-none max-w-2xl">
          {/* role="alert"/tabIndex/focus ownership moved onto `banner`'s own
              element (QuizWizard.tsx's errorCard/errorBanner) — TRA-345 needs
              a ref on the exact alert node to scroll/focus it when a new
              error arrives, which this wrapper can't provide since it has no
              knowledge of when banner's content actually changes. */}
          {banner && <div className="mb-6">{banner}</div>}
          {children}
        </div>
        <NavFooter {...nav} />
      </div>
    </PhotoSplit>
  );
}
